import "./style.scss";
import {
  IADatabaseCommentsDocument,
  IADatabaseCreateCommentDocument,
  IADatabaseSendEmailDocument,
} from "@/src/types/admissionDatabase";
import { useForm } from "react-hook-form";
import { AODSvgExport } from "@/src/svg/admissionOfficeDatabase/AODSvgExport";
import { FC, useState, useRef, useEffect } from "react";
import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { admissionDatabaseApi } from "@/src/api/axiosInstance";
import CustomDocumentPreviewModal from "@/src/components/CustomUtils/CustomDocumentPreviewModal/CustomDocumentPreviewModal";
import { useDispatch, useSelector } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { RootState } from "@/src/store";
import { clearSelectedRows } from "@/src/store/slices/AdmissionOfficeDatabase/AODatabaseTableSlice";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import { admissionDatabaseActionsConst } from "@/src/constants/AdmissionOffice/admissionOfficeDatabaseConsts";
import CustomDescriptionPopUP from "@/src/components/CustomUtils/CustomDescriptionPopUP/CustomDescriptionPopUP";

interface IAODBDocumentActions {
  loadActualData: () => void;
}

const documentActionDescriptions: Record<string, string> = {
  prev: "Preview the selected document.",
  comment: "View, add, or delete comments on the selected document.",
  download: "Download the selected document as a PDF.",
  "send-email": "Send the selected document to an email recipient.",
  delete: "Delete the selected document after confirmation.",
};

const AODBDocumentActions: FC<IAODBDocumentActions> = ({ loadActualData }) => {
  const dispatch = useDispatch();
  const { data, selectedRows } = useSelector((state: RootState) => state.AODatabaseTable);
  const selectedDoc =
    selectedRows.length === 1 ? data.find((row) => row.id != null && row.id === selectedRows[0]) : undefined;
  const isActionDisabled = selectedRows.length !== 1 || !selectedDoc;

  const [isDocumentCommented, setIsDocumentCommented] = useState(false);
  const [actualCommentsDoc, setActualCommentsDoc] = useState<
    (IADatabaseCommentsDocument | IADatabaseCreateCommentDocument)[] | undefined
  >(undefined);
  const [isModalShow, setModalShow] = useState<string | null>(null);
  const [commentValue, setCommentValue] = useState("");
  const formRef = useRef<HTMLFormElement | null>(null);
  const userOffice = localStorage.getItem("userID");

  useEffect(() => {
    setModalShow(null);
    setCommentValue("");
    if (selectedDoc) {
      setActualCommentsDoc(selectedDoc.comments);
      setIsDocumentCommented(!!selectedDoc.comments?.length);
    } else {
      setActualCommentsDoc(undefined);
      setIsDocumentCommented(false);
    }
  }, [selectedDoc?.id]);

  useEffect(() => {
    setIsDocumentCommented(!!actualCommentsDoc?.length);
  }, [actualCommentsDoc]);

  const handleSubmit = async (data: IADatabaseSendEmailDocument) => {
    if (!selectedDoc) return;
    const isConfirmed = window.confirm("Are you sure that you what to send form?");
    if (!isConfirmed) return;
    dispatch(setIsPreloyder(true));
    const obj = {
      seria: selectedDoc.document_seria,
      name: selectedDoc.whose_doc,
      ...data,
    };
    try {
      await admissionDatabaseRequests.sendEmailCurrentDocument(obj);
      dispatch(setIsPreloyder(false));
    } catch (error) {
      console.error("Ошибка при отправке документа:", error);
      dispatch(setIsPreloyder(false));
      alert("An unexpected error occurred - write to the developers");
    }
    setModalShow(null);
  };

  const handleOkClick = () => {
    formRef.current?.requestSubmit();
  };

  function SendDocumentForm({
    onSubmit,
    formRef,
  }: {
    onSubmit: (data: IADatabaseSendEmailDocument) => void;
    formRef: React.RefObject<HTMLFormElement | null>;
  }) {
    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm({
      defaultValues: {
        sender_email: "default",
        recipient_email: "",
        subject_email: "",
        body_email: "without-text",
      },
    });

    return (
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-body-form-input">
          <label>Select email</label>
          <select
            {...register("sender_email", {
              validate: (value) => value !== "default" || "Please select an email",
            })}
            defaultValue="default"
            style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none" }}
          >
            <option value="default" disabled>
              Select email
            </option>
            <option value="developer@ieu.edu.ua">developer@ieu.edu.ua</option>
            <option value="elearning@ieu.edu.ua">elearning@ieu.edu.ua</option>
          </select>
          {errors.sender_email && <p>Please select an email</p>}
        </div>
        <div className="modal-body-form-input">
          <label>Where to send</label>
          <input
            type="email"
            placeholder="Address"
            {...register("recipient_email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address",
              },
            })}
          />
          {errors.recipient_email && <p>{errors.recipient_email.message}</p>}
        </div>
        <div className="modal-body-form-input">
          <label>Subject email</label>
          <input type="text" placeholder="Subject email" {...register("subject_email", { required: true })} />
          {errors.subject_email && <p>Subject is required</p>}
        </div>
        <div className="modal-body-form-input">
          <label>Cover letter text</label>
          <select
            {...register("body_email", { required: true })}
            style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none" }}
          >
            <option value="without-text">Without text</option>
            <option value="cover-letter">Cover letter text for transcript</option>
          </select>
          {errors.body_email && <p>Select an option</p>}
        </div>
      </form>
    );
  }

  const documentAction = async (type: string) => {
    if (!selectedDoc || isActionDisabled) return;

    switch (type) {
      case "prev":
      case "comment":
      case "send-email":
        setModalShow(type);
        break;
      case "download": {
        if (!selectedDoc.pdf_path) {
          console.error("Нет пути к файлу");
          return;
        }
        const fileUrl = selectedDoc.pdf_path.startsWith("http")
          ? selectedDoc.pdf_path
          : `${admissionDatabaseApi.defaults.baseURL}${selectedDoc.pdf_path}`;
        try {
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error("Ошибка при загрузке файла");

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${selectedDoc.full_name}_${selectedDoc.title}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Ошибка при скачивании документа:", error);
        }
        break;
      }
      case "delete": {
        const isConfirmed = window.confirm("Are you sure you want to delete this document?");
        if (!isConfirmed) return;

        try {
          await admissionDatabaseRequests.deleteCurrentDocument(
            Number(selectedDoc.id),
            userOffice ? userOffice : "without login",
          );
          dispatch(clearSelectedRows());
          setModalShow(null);
          loadActualData();
        } catch (error) {
          console.error("Ошибка при удалении документа:", error);
        }
        break;
      }
    }
  };

  const handleEventCommentOk = async () => {
    if (!selectedDoc) return;
    if (commentValue.trim() !== "") {
      const isConfirmed = window.confirm("Are you sure you want to send this comment?");
      if (!isConfirmed) return;
      const now = new Date();
      const formatted = now.toLocaleString("ru-RU").replace(",", "");
      const data = {
        document_id: selectedDoc.id,
        date: formatted,
        name: selectedDoc.whose_doc,
        text: commentValue,
      };
      try {
        await admissionDatabaseRequests.createCommentCurrentDocument(data);
      } catch (error) {
        console.error("Ошибка при создании комментария:", error);
      }
      setActualCommentsDoc((prev) => [...(prev || []), data]);
      setCommentValue("");
      setModalShow(null);
    } else {
      setModalShow(null);
    }
  };

  const handleEventCommentDelete = async (id: number) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this comment?");
    if (!isConfirmed) return;
    try {
      await admissionDatabaseRequests.deleteCommentCurrentDocument({ comment_id: id });
    } catch (error) {
      console.error("Ошибка при удалении комментария:", error);
    }
    setActualCommentsDoc((prev) => (prev ? prev.filter((comment) => "id" in comment && comment.id !== id) : []));
    setModalShow(null);
  };

  const handleEventClearAllComments = async (id: number) => {
    const isConfirmed = window.confirm("Are you sure you want to delete all comments?");
    if (!isConfirmed) return;
    try {
      const response = await admissionDatabaseRequests.deleteAllCommentsCurrentDocument({ document_id: id });
      if (response !== null) {
        setActualCommentsDoc([]);
        setModalShow(null);
      }
    } catch (error) {
      console.error("Ошибка при удалении комментариев:", error);
    }
  };

  const fileUrl = selectedDoc?.pdf_path
    ? selectedDoc.pdf_path.startsWith("http")
      ? selectedDoc.pdf_path
      : `${admissionDatabaseApi.defaults.baseURL}${selectedDoc.pdf_path}`
    : null;

  const getActionIcon = (name: string) => {
    if (name === "comment" && isDocumentCommented) {
      return <AODSvgExport.AODHasCommentDocSvg />;
    }
    const action = admissionDatabaseActionsConst.find((item) => item.name === name);
    return action ? <action.icon /> : null;
  };

  return (
    <>
      {admissionDatabaseActionsConst.map((action) => (
        <CustomDescriptionPopUP key={action.name} text={documentActionDescriptions[action.name] ?? action.name}>
          <BtnDefault
            onlyIcon={true}
            disabled={isActionDisabled}
            onClick={() => documentAction(action.name)}
          >
            {getActionIcon(action.name)}
          </BtnDefault>
        </CustomDescriptionPopUP>
      ))}

      <CustomDocumentPreviewModal
        isOpen={isModalShow === "prev" && Boolean(fileUrl)}
        documentUrl={fileUrl}
        onClose={() => setModalShow(null)}
        title={`${selectedDoc?.full_name ?? ""} — ${selectedDoc?.title ?? ""}`}
      />

      {isModalShow === "comment" && selectedDoc && (
        <div className="document-actions-modal comment-modal header-actions-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body">
            <div className="modal-body-header">
              <p className="modal-body-header-title">Comments</p>
              <p
                className="modal-body-header-clear"
                onClick={() => selectedDoc.id && handleEventClearAllComments(selectedDoc.id)}
              >
                Clear all comments
              </p>
            </div>
            <div className="modal-body-comments">
              {actualCommentsDoc?.length
                ? actualCommentsDoc.map((comment, index) => (
                    <div className="modal-body-comments-comment" key={index}>
                      <div className="modal-body-comments-comment-text">
                        <p>
                          <span>{comment.date}</span>
                        </p>
                        <p>
                          <span>{comment.name}</span> {comment.text}
                        </p>
                      </div>
                      {"id" in comment && (
                        <div
                          className="modal-body-comments-comment-delete"
                          onClick={() => handleEventCommentDelete(comment.id!)}
                        >
                          <AODSvgExport.AODDeleteDocSvg />
                        </div>
                      )}
                    </div>
                  ))
                : null}
            </div>
            <div className="modal-body-input">
              <input
                type="text"
                placeholder="Comment..."
                value={commentValue}
                onChange={(e) => setCommentValue(e.target.value)}
              />
            </div>
            <div className="modal-body-buttons">
              <div className="modal-body-buttons-ok" onClick={handleEventCommentOk}>
                Ok
              </div>
              <div className="modal-body-buttons-cancel" onClick={() => setModalShow(null)}>
                Cancel
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalShow === "send-email" && selectedDoc && (
        <div className="document-actions-modal email-modal header-actions-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body">
            <div className="modal-body-header">
              <p className="modal-body-header-title">Sending a document</p>
            </div>
            <div className="modal-body-form">
              <SendDocumentForm onSubmit={handleSubmit} formRef={formRef} />
            </div>
            <div className="modal-body-buttons">
              <div className="modal-body-buttons-ok" onClick={handleOkClick}>
                Ok
              </div>
              <div className="modal-body-buttons-cancel" onClick={() => setModalShow(null)}>
                Cancel
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AODBDocumentActions;
